import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, ExternalLink } from "lucide-react";

// Lightweight editor for the public showcase profile.
// Owners type their slug (e.g. "inochi") and edit the Overview block.
// Power-users can extend later — saved payload is shallow-merged with the static defaults.
export default function VendorProfileEditor() {
  const [slug, setSlug] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [category, setCategory] = useState("");
  const [origin, setOrigin] = useState("");
  const [intro, setIntro] = useState("");
  const [brandStory, setBrandStory] = useState("");
  const [marketOpportunity, setMarketOpportunity] = useState("");
  const [highlights, setHighlights] = useState("");
  const [heroImage, setHeroImage] = useState("");
  const [published, setPublished] = useState(true);
  const [profiles, setProfiles] = useState([]);

  // Load my profiles on mount
  useEffect(() => {
    api.get("/vendor-profiles-mine/me")
      .then(({ data }) => {
        setProfiles(data || []);
        if (data && data[0]) loadProfileInto(data[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfileInto = (p) => {
    setSlug(p.slug || "");
    const o = p.overview || {};
    setName(o.name || "");
    setTagline(o.tagline || "");
    setCategory(o.category || "");
    setOrigin(o.origin || "");
    setIntro(o.intro || "");
    setBrandStory(o.brandStory || "");
    setMarketOpportunity(o.marketOpportunity || "");
    setHighlights((o.highlights || []).join("\n"));
    setHeroImage(o.heroImage || "");
    setPublished(p.published !== false);
    setLoaded(true);
  };

  const loadBySlug = async () => {
    if (!slug) return;
    try {
      // Try fetching as the canonical (published) view first
      const { data } = await api.get(`/vendor-profiles/${slug}`);
      loadProfileInto(data);
      toast.success("Loaded");
    } catch (err) {
      // Not found — start fresh with this slug
      setLoaded(true);
      toast.info("New profile — fill in details and save");
    }
  };

  const save = async () => {
    if (!slug) { toast.error("Pick a URL slug (e.g. inochi)"); return; }
    setBusy(true);
    const payload = {
      published,
      overview: {
        name, tagline, category, origin, intro, brandStory, marketOpportunity,
        highlights: highlights.split("\n").map((s) => s.trim()).filter(Boolean),
        heroImage,
      },
    };
    try {
      await api.put(`/vendor-profiles-mine/${slug}`, payload);
      toast.success("Saved — refresh /showcase/" + slug + " to see updates");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-zinc-200 bg-white" data-testid="vendor-profile-editor">
      <div className="p-4 border-b border-zinc-200">
        <div className="overline text-zinc-500">My showcase profile</div>
        <h2 className="font-display font-black text-xl tracking-tight">Edit your public showcase page</h2>
        <p className="text-xs text-zinc-600 mt-1">Your content goes live at <span className="font-mono">/showcase/&lt;slug&gt;</span> the moment you save. Photos and videos come from the Media Manager above.</p>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">URL slug</label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              data-testid="vendor-slug"
              placeholder="e.g. inochi"
              className="w-full mt-1 px-3 py-2 border border-zinc-300 text-sm outline-none focus:border-[#C8102E]"
            />
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">→ {window.location.origin}/showcase/{slug || "<slug>"}</p>
          </div>
          <button
            onClick={loadBySlug}
            disabled={!slug}
            data-testid="vendor-load-slug"
            className="px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50"
          >
            Load / Start
          </button>
        </div>

        {profiles.length > 0 && (
          <div className="text-xs text-zinc-600">
            Your existing slugs: {profiles.map((p) => (
              <button key={p.slug} onClick={() => loadProfileInto(p)} className="font-mono text-[#C8102E] hover:underline mr-2">{p.slug}</button>
            ))}
          </div>
        )}

        {loaded && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Brand name"        value={name}     onChange={setName}    placeholder="INOCHI" testid="vp-name" />
              <Field label="Tagline"           value={tagline}  onChange={setTagline} placeholder="Power of Life · Japanese Anti-Aging Beauty" testid="vp-tagline" />
              <Field label="Category"          value={category} onChange={setCategory} placeholder="Health & Beauty · Premium Anti-Aging Skincare" testid="vp-category" />
              <Field label="Origin"            value={origin}   onChange={setOrigin}   placeholder="Japan · Partnership with…" testid="vp-origin" />
              <Field label="Hero image URL"    value={heroImage} onChange={setHeroImage} placeholder="/brand/inochi.png" testid="vp-hero" full />
            </div>

            <Field label="Short intro"             value={intro}             onChange={setIntro}             placeholder="One-paragraph intro." testid="vp-intro" textarea />
            <Field label="Brand story"             value={brandStory}        onChange={setBrandStory}        placeholder="2-3 paragraphs of brand origin and ethos." testid="vp-story" textarea />
            <Field label="Market opportunity"      value={marketOpportunity} onChange={setMarketOpportunity} placeholder="Why this product is in demand." testid="vp-market" textarea />
            <Field label="Highlights (one per line)" value={highlights}      onChange={setHighlights}        placeholder="Partnership with Picaso Japan since 1935\nNano-Encapsulation Technology" testid="vp-highlights" textarea />

            <label className="flex items-center gap-2 text-sm" data-testid="vp-published">
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="w-4 h-4" />
              <span>Published (visible to public). Uncheck to hide while editing.</span>
            </label>

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-200">
              <button
                onClick={save}
                disabled={busy}
                data-testid="vp-save"
                className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
              </button>
              {slug && (
                <a
                  href={`/showcase/${slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="vp-preview"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100"
                >
                  Preview public page <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, testid, textarea, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          rows={4}
          placeholder={placeholder}
          className="w-full mt-1 px-3 py-2 border border-zinc-300 text-sm outline-none focus:border-[#C8102E] resize-y"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          className="w-full mt-1 px-3 py-2 border border-zinc-300 text-sm outline-none focus:border-[#C8102E]"
        />
      )}
    </div>
  );
}
